import React from "react";
import ModalForm from "@ui/Modal/Form";
import t from "../../i18n";
import type { Project } from "../../api/useProjects";
import { useProjects } from "../../api/useProjects";
import useInput from "@ui/lib/hooks/useInput";
import InputV2 from "@ui/Input/InputV2";
import Checkbox from "@ui/Checkbox";
import { usePost } from "../../api/useCall";
import { isLeft } from "fp-ts/lib/Either";
import { short } from "diagnostics-lib/io";
import * as io from "io-ts";
import AdditionnalMessage from "@ui/AdditionnalMessage";
import { useFaultEventAccess } from "../Project/Events/hooks/useFaultEventAccess";
import { useTranslations } from "../../api/useTranslations";

interface P {
  project: Project;
  onRequestClose: () => void;
}

const DuplicateProjectModalForm = ({ project, onRequestClose }: P) => {
  const projects = useProjects();
  const hasFaultEventAcces = useFaultEventAccess();
  const duplicateProject = usePost("/projects/:projectId(uuid)/duplicate");
  const nameInput = useInput(t("projects.modal.duplicate.duplicated_name", { name: project.name }));
  const [includeComments, setIncludeComments] = React.useState(true);
  const [includeDashboards, setIncludeDashboards] = React.useState(true);
  const [includeEvents, setIncludeEvents] = React.useState(true);
  const [includeUserProjectRoles, setIncludeUserProjectRoles] = React.useState(true);
  const translations = useTranslations();

  const inputNameError = React.useMemo(() => {
    if (nameInput.value === "")
      return t("projects.modal.create_or_edit.input.name.error.cannot_be_empty");

    if (isLeft(short(io.string).decode(nameInput.value)))
      return t("projects.modal.create_or_edit.input.name.error.too_long");

    if (projects.some(p => p.name === nameInput.value.trim()))
      return t("projects.modal.create_or_edit.input.name.error.name_already_exists");

    return;
  }, [projects, nameInput.value]);

  const handleSubmit = React.useCallback(async () => {
    if (inputNameError) return false;
    await duplicateProject({
      routeParams: { projectId: project.id },
      body: {
        targetProjectName: nameInput.value,
        includeComments,
        includeDashboards,
        includeEvents,
        includeUserProjectRoles,
      },
    });
    return true;
  }, [
    duplicateProject,
    includeComments,
    includeDashboards,
    includeEvents,
    includeUserProjectRoles,
    inputNameError,
    nameInput.value,
    project.id,
  ]);

  return (
    <ModalForm
      onRequestClose={onRequestClose}
      okButtonName={t("modalForm.button.name.duplicate")}
      onSubmit={inputNameError ? false : () => handleSubmit()}
      focusCancel
    >
      <InputV2
        label={t("projects.modal.update_projects.title.name")}
        error={inputNameError}
        placeholder={t("projects.modal.delete.input_placeholder")}
        {...nameInput}
        revertable={false}
      />
      <Checkbox
        checked={includeComments}
        onChange={e => setIncludeComments(e.currentTarget.checked)}
      >
        {t("projects.modal.duplicate.input.include_comments")}
      </Checkbox>
      <Checkbox
        checked={includeDashboards}
        onChange={e => setIncludeDashboards(e.currentTarget.checked)}
      >
        {t("projects.modal.duplicate.input.include_dashboards", translations)}
      </Checkbox>

      {hasFaultEventAcces && (
        <>
          <Checkbox
            checked={includeEvents}
            onChange={e => setIncludeEvents(e.currentTarget.checked)}
          >
            {t("projects.modal.duplicate.input.include_events")}
          </Checkbox>
          {!includeEvents && (
            <AdditionnalMessage
              status="INFO"
              text={t("projects.modal.duplicate.input.additional_message_info.deleted_comments")}
            />
          )}
        </>
      )}
      <Checkbox
        checked={includeUserProjectRoles}
        onChange={e => setIncludeUserProjectRoles(e.currentTarget.checked)}
      >
        {t("projects.modal.duplicate.input.include_user_project_roles")}
      </Checkbox>
    </ModalForm>
  );
};

export default React.memo(DuplicateProjectModalForm);
